import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { partnerKeys } from '@/api/query-keys';
import {
  createPartnerContactContract,
  deletePartnerContactContract,
  importPartnerContactsContract,
  updatePartnerContactContract,
  type CreatePartnerContactRequest,
  type CreatePartnerContactResponse,
  type DeletePartnerContactResponse,
  type ImportPartnerContactsRequest,
  type ImportPartnerContactsResponse,
  type UpdatePartnerContactRequest,
  type UpdatePartnerContactResponse
} from '@bliks-io/contracts';

export function useCreatePartnerContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: CreatePartnerContactRequest
    ): Promise<CreatePartnerContactResponse> => {
      const { data: response } = await apiClient.post<CreatePartnerContactResponse>(
        createPartnerContactContract.path,
        data
      );
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerKeys.contacts() })
  });
}

export function useImportPartnerContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: ImportPartnerContactsRequest
    ): Promise<ImportPartnerContactsResponse> => {
      const { data: response } = await apiClient.post<ImportPartnerContactsResponse>(
        importPartnerContactsContract.path,
        data
      );
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerKeys.contacts() })
  });
}

export function useUpdatePartnerContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdatePartnerContactRequest;
    }): Promise<UpdatePartnerContactResponse> => {
      const { data: response } = await apiClient.patch<UpdatePartnerContactResponse>(
        updatePartnerContactContract.path.replace(':id', id),
        data
      );
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerKeys.contacts() })
  });
}

export function useDeletePartnerContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeletePartnerContactResponse> => {
      const { data } = await apiClient.delete<DeletePartnerContactResponse>(
        deletePartnerContactContract.path.replace(':id', id)
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerKeys.contacts() })
  });
}
